import { type Editor, useEditorState } from '@tiptap/react';
import {
  Bold,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { HIGHLIGHT_COLOR } from '@avanta/shared/utils/text';
import { LinkButton } from './components/LinkButton';
import { ToolbarButton } from './components/ToolbarButton';

const TOOLBAR_ACTION = {
  BOLD: 'bold',
  ITALIC: 'italic',
  STRIKE: 'strike',
  UNDERLINE: 'underline',
  HIGHLIGHT: 'highlight',
  BULLET_LIST: 'bulletList',
  ORDERED_LIST: 'orderedList',
  LINK: 'link',
} as const;

const TOOLBAR_ACTIONS = [
  {
    id: TOOLBAR_ACTION.BOLD,
    icon: Bold,
    getActive: (editor: Editor): boolean => editor.isActive(TOOLBAR_ACTION.BOLD),
    toggle: (editor: Editor): boolean => editor.chain().focus().toggleBold().run(),
  },
  {
    id: TOOLBAR_ACTION.ITALIC,
    icon: Italic,
    getActive: (editor: Editor): boolean => editor.isActive(TOOLBAR_ACTION.ITALIC),
    toggle: (editor: Editor): boolean => editor.chain().focus().toggleItalic().run(),
  },
  {
    id: TOOLBAR_ACTION.STRIKE,
    icon: Strikethrough,
    getActive: (editor: Editor): boolean => editor.isActive(TOOLBAR_ACTION.STRIKE),
    toggle: (editor: Editor): boolean => editor.chain().focus().toggleStrike().run(),
  },
  {
    id: TOOLBAR_ACTION.UNDERLINE,
    icon: Underline,
    getActive: (editor: Editor): boolean => editor.isActive(TOOLBAR_ACTION.UNDERLINE),
    toggle: (editor: Editor): boolean => editor.chain().focus().toggleUnderline().run(),
  },
  {
    id: TOOLBAR_ACTION.HIGHLIGHT,
    icon: Highlighter,
    getActive: (editor: Editor): boolean =>
      editor.isActive(TOOLBAR_ACTION.HIGHLIGHT, { color: HIGHLIGHT_COLOR }),
    toggle: (editor: Editor): boolean =>
      editor.chain().focus().toggleHighlight({ color: HIGHLIGHT_COLOR }).run(),
  },
  {
    id: TOOLBAR_ACTION.BULLET_LIST,
    icon: List,
    getActive: (editor: Editor): boolean => editor.isActive(TOOLBAR_ACTION.BULLET_LIST),
    toggle: (editor: Editor): boolean => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: TOOLBAR_ACTION.ORDERED_LIST,
    icon: ListOrdered,
    getActive: (editor: Editor): boolean => editor.isActive(TOOLBAR_ACTION.ORDERED_LIST),
    toggle: (editor: Editor): boolean => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: TOOLBAR_ACTION.LINK,
    render: (editor: Editor): React.ReactElement => <LinkButton editor={editor} />,
  },
];

export const Toolbar = ({ editor }: { editor: Editor }): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const activeState = useEditorState({
    editor,
    selector: ({ editor: activeEditor }) =>
      Object.fromEntries(
        TOOLBAR_ACTIONS.map(item => [
          item.id,
          item.getActive ? item.getActive(activeEditor) : false,
        ])
      ),
  });

  return (
    <div className="flex items-center border-b border-gray-100 pb-1 mb-1 w-fit">
      {TOOLBAR_ACTIONS.map(item =>
        item.render ? (
          <Fragment key={item.id}>{item.render(editor)}</Fragment>
        ) : (
          <ToolbarButton
            key={item.id}
            label={t(`Chat.RichTextInput.${item.id}`)}
            icon={item.icon}
            active={activeState[item.id]}
            onClick={() => item.toggle(editor)}
          />
        )
      )}
    </div>
  );
};
